import { useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { promesaDelGancho } from '../onboarding-v2/quinceObligatorias'

/**
 * LandingPagePL — home pública v3 (EL CIERRE · G0 Fuente Única).
 *
 * Porta la landing estática de G0 (`landing/index.html`) DENTRO del SPA: el HTML
 * estático es la fuente del contenido/diseño; esta es su encarnación React con los
 * tokens y el routing in-app. La landing vieja (features Target/Salad/Shuffle) MUERE.
 *
 * Hero glow sobre ink · claim · 3 tesis (bucle vivo · ciencia citada · con/sin
 * wearable) · CTA in-SPA a signup · footer honesto (alpha). Voz aurIA · cero hype.
 *
 * LandingOrToday (App.js) gobierna el routing: sin auth → esta landing · con auth →
 * /today · onboarding incompleto → /onboarding. pl_has_seen_landing = analítica (NO skip).
 */

/** Marca analítica · engagement con landing. NO se usa para routing (PWA icon SIEMPRE
 *  muestra landing para unauth · doctrina #13 primera impresión). */
function markLandingSeen() {
  try { localStorage.setItem('pl_has_seen_landing', 'true') } catch (_) {}
}

const THESIS = [
  {
    k: 'El bucle vivo',
    title: 'Prescribe · vives · aprende',
    body: (<>Cada día el sistema te propone qué hacer. Tú lo vives. Y lo que pasa —cómo dormiste, qué te costó, qué comiste— <strong>afina el plan de mañana</strong>. No decides tú los detalles: los orquesta el motor.</>),
  },
  {
    k: 'Ciencia citada',
    title: 'Cada decisión responde a "¿quién firma esto?"',
    body: (<>Detrás de cada recomendación hay <strong>un estudio, no una moda</strong>. El sistema conoce la fuente de todo lo que te propone — aunque tú nunca tengas que verla.</>),
  },
  {
    k: 'Con o sin wearable',
    title: 'Funciono entero con lo que me cuentas',
    body: (<>Si tienes un wearable, afino la precisión. Si no, <strong>el sistema es igual de completo</strong>. No hay versión premium con banda ni funciones que se compran: el producto es uno solo.</>),
  },
]

export default function LandingPagePL() {
  const navigate = useNavigate()

  const handleScroll = useCallback(() => {
    if (window.scrollY > 80) {
      markLandingSeen()
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  function handleCTA() { markLandingSeen(); navigate('/auth?mode=signup') }
  function handleHaveAccount() { markLandingSeen(); navigate('/auth?mode=login') }

  return (
    <div data-theme="light" style={{ background: 'var(--pl-cream)', color: 'var(--pl-ink)', fontFamily: 'var(--font-sans)', colorScheme: 'light' }}>
      {/* ── HERO · símbolo glow sobre ink ── */}
      <section style={{
        background: 'var(--pl-ink)', color: 'var(--pl-cream)', textAlign: 'center',
        minHeight: '100vh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', padding: '72px 24px',
      }}>
        <motion.img
          src="/logo-mark.svg" alt="" aria-hidden width={92} height={92}
          initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          style={{ filter: 'var(--pl-glow-terracota)' }}
        />
        <motion.h1
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          style={{ margin: '36px 0 0', fontSize: 'clamp(1.9rem,5.2vw,3.2rem)', fontWeight: 800, lineHeight: 1.12, letterSpacing: '-0.02em', maxWidth: '16ch' }}
        >
          El conocimiento para vivir en tu mejor versión ya existe. <span style={{ color: 'var(--pl-terracota)' }}>Ahora tiene sistema.</span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.28 }}
          style={{ margin: 'clamp(20px,4vh,28px) 0 0', fontSize: 'clamp(1rem,2.4vw,1.2rem)', color: 'color-mix(in srgb, var(--pl-cream) 82%, var(--pl-ink))', maxWidth: '44ch', lineHeight: 1.55 }}
        >
          PrimeLab orquesta entrenamiento, nutrición, sueño y recuperación en un solo motor que se adapta a tu vida real — no al revés.
        </motion.p>
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.42 }}
          style={{ marginTop: 'clamp(28px,5vh,40px)', width: '100%', maxWidth: 340 }}
        >
          <button type="button" onClick={handleCTA} style={{
            width: '100%', background: 'var(--pl-terracota)', color: 'var(--pl-cream)', border: 'none',
            fontWeight: 600, fontSize: '1.05rem', padding: '16px 34px', borderRadius: 999,
            cursor: 'pointer', fontFamily: 'inherit',
          }}>Empezar</button>
          {/* SALTO:onboarding-10 · T8 · la MISMA promesa que el gancho del alta, del mismo sitio: la landing es la
              primera pantalla que ve un desconocido, y prometía tres minutos sobre un alta cronometrada en 7 y medio.
              Sale de la medida (`quinceObligatorias.promesaDelGancho`), así que vuelve sola a «menos de 3» cuando lo sea. */}
          <p style={{ margin: '12px 0 0', fontSize: 12, color: 'color-mix(in srgb, var(--pl-cream) 62%, var(--pl-ink))' }}>{promesaDelGancho().replace(/^en /, '').replace(/^unos/, 'Unos').replace(/^menos/, 'Menos')} · sin tarjeta.</p>
          <button type="button" onClick={handleHaveAccount} style={{
            marginTop: 18, background: 'transparent', border: 'none', cursor: 'pointer',
            color: 'color-mix(in srgb, var(--pl-cream) 82%, var(--pl-ink))', fontSize: 13, textDecoration: 'underline', padding: 0, fontFamily: 'inherit',
          }}>Ya tengo cuenta · entrar</button>
        </motion.div>
      </section>

      {/* ── TESIS · 3 bloques ── */}
      <section style={{ padding: 'clamp(56px,10vh,104px) 24px', maxWidth: 1080, margin: '0 auto' }}>
        <h2 style={{ textAlign: 'center', fontSize: 'clamp(1.4rem,3.4vw,2rem)', fontWeight: 700, letterSpacing: '-0.015em', maxWidth: '20ch', margin: '0 auto clamp(40px,7vh,64px)', color: 'var(--pl-ink)' }}>
          No es un tracker que anota. Es un motor que decide contigo.
        </h2>
        <div className="pl-landing-thesis" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 'clamp(20px,3vw,32px)' }}>
          {THESIS.map(({ k, title, body }) => (
            <article key={k} style={{ background: 'var(--pl-surface)', border: '1px solid var(--pl-cream-soft)', borderRadius: 20, padding: 'clamp(28px,3.5vw,36px)' }}>
              <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--pl-terracota-700)' }}>{k}</span>
              <h3 style={{ fontSize: '1.28rem', fontWeight: 700, letterSpacing: '-0.01em', margin: '12px 0 10px', color: 'var(--pl-ink)' }}>{title}</h3>
              <p style={{ color: 'var(--pl-muted-pl)', fontSize: '1.01rem', lineHeight: 1.55, margin: 0 }}>{body}</p>
            </article>
          ))}
        </div>
      </section>

      {/* ── CIERRE ── */}
      <section style={{ textAlign: 'center', padding: 'clamp(48px,9vh,96px) 24px', background: 'var(--pl-cream-soft)' }}>
        <h2 style={{ fontSize: 'clamp(1.5rem,3.8vw,2.2rem)', fontWeight: 800, letterSpacing: '-0.015em', maxWidth: '18ch', margin: '0 auto', color: 'var(--pl-ink)' }}>
          Tu mejor versión, diseñada por ciencia.
        </h2>
        <p style={{ color: 'var(--pl-muted-pl)', margin: '16px auto 0', maxWidth: '40ch' }}>Empieza tu sistema hoy. Se adapta a ti desde el primer día.</p>
        <button type="button" onClick={handleCTA} style={{
          marginTop: 28, background: 'var(--pl-terracota)', color: 'var(--pl-cream)', border: 'none',
          fontWeight: 600, fontSize: '1.05rem', padding: '15px 34px', borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit',
        }}>Empezar ahora</button>
      </section>

      {/* ── footer honesto (alpha) ── */}
      <footer style={{ borderTop: '1px solid var(--pl-cream-soft)', padding: '40px 24px', color: 'var(--pl-hint)', fontSize: '0.86rem', textAlign: 'center' }}>
        PrimeLab está en <strong>alpha privada</strong>. Lo construimos con cuidado, sin prisa por prometer de más.
      </footer>

      <style>{`
        @media (min-width: 768px) {
          .pl-landing-thesis { grid-template-columns: repeat(3, 1fr) !important; }
        }
      `}</style>
    </div>
  )
}
